import { useState, useEffect } from "react";
import axios from "axios";
import {
  TbBuildingStore, TbPlus, TbToggleLeft, TbToggleRight,
  TbExternalLink, TbMapPin, TbHome, TbRefresh,
  TbSearch, TbAlertCircle
} from "react-icons/tb";

const API = "https://api.komorotel.com/api/agency";

const TYPE_LABELS = {
  hotel:               "Hôtel",
  chambre_hote:        "Chambre d'hôte",
  appartement_studio:  "Appartement / Studio",
};

function HotelCard({ hotel, onToggle, onAccess, toggling }) {
  return (
    <div className={`bg-white border rounded-2xl p-4 shadow-sm flex flex-col gap-3 transition-opacity ${!hotel.active ? "opacity-60" : ""}`}>

      {/* Photo */}
      <div className="w-full h-36 rounded-xl overflow-hidden bg-gray-100 relative">
        {hotel.photos?.[0]
          ? <img src={hotel.photos[0]} alt={hotel.name} className="w-full h-full object-cover" />
          : (
            <div className="w-full h-full flex items-center justify-center">
              <TbHome className="text-gray-300" size={32} />
            </div>
          )
        }
        {/* Badge type */}
        <span className="absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/90 text-gray-600 border border-gray-100">
          {TYPE_LABELS[hotel.type] || hotel.type}
        </span>
        {/* Badge statut */}
        <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${hotel.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
          {hotel.active ? "Actif" : "Inactif"}
        </span>
      </div>

      {/* Infos */}
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900 truncate">{hotel.name}</p>
        <div className="flex items-center gap-1 mt-0.5">
          <TbMapPin size={12} className="text-gray-400 shrink-0" />
          <p className="text-xs text-gray-400 truncate">{hotel.city}, {hotel.island}</p>
        </div>
        {hotel.minPrice && (
          <p className="text-xs text-[var(--k-green)] font-medium mt-1">
            À partir de {hotel.minPrice.toLocaleString("fr-FR")} €
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
        {/* Toggle actif/inactif */}
        <button
          onClick={() => onToggle(hotel)}
          disabled={toggling === hotel._id}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition disabled:opacity-50"
        >
          {hotel.active
            ? <TbToggleRight size={18} className="text-emerald-500" />
            : <TbToggleLeft size={18} className="text-gray-400" />
          }
          {toggling === hotel._id ? "..." : hotel.active ? "Désactiver" : "Activer"}
        </button>

        <div className="flex-1" />

        {/* Accéder au back-office */}
        <button
          onClick={() => onAccess(hotel)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--k-green)] text-white text-xs font-medium hover:bg-[var(--k-green-hover)] transition"
        >
          <TbExternalLink size={13} />
          Gérer
        </button>
      </div>
    </div>
  );
}

export default function AgencyHotels({ setActivePage }) {
  const [hotels, setHotels]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [toggling, setToggling] = useState(null);
  const [search, setSearch]   = useState("");
  const [filter, setFilter]   = useState("all"); // all | active | inactive

  const fetchHotels = () => {
    setLoading(true);
    setError(null);
    axios.get(`${API}`, { withCredentials: true })
      .then(r => setHotels(r.data))
      .catch(() => setError("Impossible de charger les biens."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHotels(); }, []);

  const handleToggle = async (hotel) => {
    setToggling(hotel._id);
    try {
      await axios.patch(`${API}/${hotel._id}/toggle`, {}, { withCredentials: true });
      setHotels(h => h.map(x => x._id === hotel._id ? { ...x, active: !x.active } : x));
    } catch {
      alert("Erreur lors de la mise à jour.");
    } finally {
      setToggling(null);
    }
  };

  const handleAccess = async (hotel) => {
    try {
      await axios.post(`${API}/hotels/${hotel._id}/access`, {}, { withCredentials: true });
      // Redirige vers le back-office hôtelier existant
      window.location.href = `/agency/backoffice/${hotel._id}`;
    } catch {
      alert("Impossible d'accéder au back-office.");
    }
  };

  const filtered = hotels
    .filter(h => filter === "all" ? true : filter === "active" ? h.active : !h.active)
    .filter(h => h.name.toLowerCase().includes(search.toLowerCase()) ||
                 h.city.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Mes biens</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {hotels.length} bien{hotels.length > 1 ? "s" : ""} dans votre portefeuille
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchHotels}
            className="w-9 h-9 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm hover:bg-gray-50 transition text-gray-400"
          >
            <TbRefresh size={16} />
          </button>
          <button
            onClick={() => setActivePage("create-hotel")}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--k-green)] text-white text-sm font-medium rounded-xl hover:bg-[var(--k-green-hover)] transition shadow-sm"
          >
            <TbPlus size={16} />
            Ajouter un bien
          </button>
        </div>
      </div>

      {/* ── FILTRES ── */}
      <div className="flex items-center gap-3">
        {/* Recherche */}
        <div className="relative flex-1 max-w-xs">
          <TbSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un bien..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
          />
        </div>

        {/* Filtre statut */}
        <div className="flex items-center gap-1 bg-white border border-gray-100 rounded-xl p-1">
          {[
            { key: "all",      label: "Tous" },
            { key: "active",   label: "Actifs" },
            { key: "inactive", label: "Inactifs" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filter === f.key
                  ? "bg-[var(--k-green)] text-white"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── CONTENU ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-gray-400">Chargement...</p>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
          <TbAlertCircle className="text-red-500 shrink-0" size={18} />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
            <TbBuildingStore className="text-gray-400" size={24} />
          </div>
          <p className="text-sm font-medium text-gray-500">
            {search ? "Aucun bien trouvé" : "Aucun bien dans le portefeuille"}
          </p>
          {!search && (
            <button
              onClick={() => setActivePage("create-hotel")}
              className="text-xs text-[var(--k-green)] hover:underline"
            >
              Ajouter votre premier bien →
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(hotel => (
            <HotelCard
              key={hotel._id}
              hotel={hotel}
              onToggle={handleToggle}
              onAccess={handleAccess}
              toggling={toggling}
            />
          ))}
        </div>
      )}
    </div>
  );
}